/**
 * Native shell — web-absolute link guard.
 *
 * The web app lives under `/v2` (router basename '/v2'); the shell serves at
 * `capacitor://localhost` with basename '/'. ~20 call sites render plain
 * `<a href="/v2/…">` anchors (TodayHero plan links, hero CTAs, …). On web the
 * server rewrites those to the SPA; in the shell a raw anchor click is a FULL
 * page-load to a file that doesn't exist → WHITE SCREEN with no way back
 * (founder, 2026-07-09: Today → "Open week plan" force-close).
 *
 * This mount (native-only, renders null) intercepts same-app `/v2/…` anchor
 * clicks and routes them through the SPA router with the prefix stripped.
 * Router-generated links never match (basename '/' means they carry no `/v2`),
 * so this touches ONLY the hardcoded web-absolute hrefs — one guard instead of
 * twenty fragile call-site edits.
 *
 * ⚠️ It listens in the BUBBLE phase, deliberately, and it YIELDS to any anchor
 * React already handled. See the comment on the listener registration below —
 * that choice is a fix for a shipped field bug, not a style preference.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isNativeApp } from '../lib/platform';

export function NativeLinkGuard(): null {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNativeApp()) return;

    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = (e.target as Element | null)?.closest?.('a');
      if (!anchor) return;
      if (anchor.target && anchor.target !== '_self') return;
      const href = anchor.getAttribute('href');
      if (!href || !(href === '/v2' || href.startsWith('/v2/') || href.startsWith('/v2?'))) return;
      e.preventDefault();
      navigate(href.slice('/v2'.length) || '/');
    };

    // ⚖️ BUBBLE phase — and the phase is load-bearing (field bug, 2026-08-10).
    //
    // This listener used to run in CAPTURE, justified as "beat the browser's
    // default navigation". That reasoning is wrong: `preventDefault()` works
    // anywhere during dispatch, so BUBBLE beats the default action too. What
    // capture actually did was beat REACT — React 17+ delegates to the root
    // container, which is INSIDE document, so a document-capture listener runs
    // before every React onClick in the app.
    //
    // The cost was a silent one. `CoachLine`'s reaction tap is an `/v2/chat?
    // …&run=<id>` anchor whose onClick mints the §2.1 activation nonce and
    // navigates WITH state. In capture this guard got there first, navigated
    // with NO state, and re-rendered the tree away before React's handler
    // could run — so the coach's post-run note opened an ordinary, contextless
    // chat. URL correct, context gone. Device trace: one pushState, `usr:
    // null`, no second navigation.
    //
    // In BUBBLE the ordering is right, and the `e.defaultPrevented` check at
    // the top of this handler — dead code until now, since nothing could
    // precede a document-capture listener — finally does its job:
    //   · anchor WITH a React onClick → React runs first, prevents default,
    //     navigates with its own state; this guard sees it and stands down.
    //   · raw anchor (no React handler) → nothing prevents default; this guard
    //     catches it exactly as before, still ahead of the browser.
    //
    // The yield case is not hypothetical. `team/FeedRow.tsx` is an anchor
    // whose href (`/v2/team/activity/<id>`) is an UNREGISTERED route, held
    // back on purpose by its own `preventDefault` while the real behavior is
    // opening a profile drawer. In CAPTURE this guard won that race too and
    // navigated the shell to a 404 — a second native-only bug, fixed by the
    // same reordering and pinned in CoachLine.nativeTap.repro.test.tsx.
    //
    // ⚠️ STANDING LIMITATION, recorded so it is a known edge and not a
    // surprise: a `/v2/…` anchor rendered INSIDE a subtree that calls
    // `stopPropagation()` on click never reaches this listener (React's
    // synthetic stopPropagation calls the native one), so it would lose its
    // rescue. Audited 2026-08-10 — no such anchor exists today; the six
    // stopPropagation sites are modal wrappers and buttons with no `/v2`
    // anchor inside. Re-check when adding one.
    document.addEventListener('click', onClick, false);
    return () => document.removeEventListener('click', onClick, false);
  }, [navigate]);

  return null;
}
